import { z } from "zod";
import { connectorNameParamsSchema } from "./connectors";

/**
 * Which connectors a NEW connection may use (T-51, round 8; a customer asked to see 4 of 20).
 *
 * The connectors offered in an instance are the platform's available connectors, intersected
 * with the tenant's choice:
 *
 * 1. Platform (PlatformAdmin, `platform.connectors.manage`): a connector can be made "Not
 *    available" for every tenant (a deprecated or preview one). One `platform_settings` row per
 *    connector (`connectorAvailabilityKey`), apart from `connector_definitions`, so a re-seed
 *    never resets it. An inactive definition is not available either.
 * 2. Tenant: `all` ("All available connectors", the default) or `selected` (only the named ones,
 *    so a connector added later is not offered). A TenantAdmin manages it (`managedBy: "tenant"`)
 *    unless a PlatformAdmin locked it (`managedBy: "platform"`, "Locked by Skypoint"): then the
 *    PlatformAdmin's list applies and the TenantAdmin reads it only. One `platform_settings` row
 *    per tenant (`tenantConnectorAccessKey`).
 *
 * A connector the platform switches off disappears from a tenant's offer even when it is on the
 * tenant's list, and comes back when the platform switches it on again and it is still listed.
 * Only a new connection is affected: an existing connection keeps working whatever its connector
 * (view, edit, test, run, schedules, retries). `server/services/connectorAccess.ts` reads and
 * writes all of it.
 */

export const CONNECTOR_ACCESS_MODES = ["all", "selected"] as const;
export type ConnectorAccessMode = (typeof CONNECTOR_ACCESS_MODES)[number];

/** Who manages a tenant's choice: the TenantAdmin (default), or Skypoint ("Locked by Skypoint"). */
export const CONNECTOR_ACCESS_MANAGERS = ["tenant", "platform"] as const;
export type ConnectorAccessManager = (typeof CONNECTOR_ACCESS_MANAGERS)[number];

/** A tenant that has never chosen offers every available connector, as before T-51. */
export const CONNECTOR_ACCESS_DEFAULT_MODE: ConnectorAccessMode = "all";
export const CONNECTOR_ACCESS_DEFAULT_MANAGER: ConnectorAccessManager = "tenant";

/** More than the catalog will hold (NFR-5 sizes it at fifty); a bound, not a product limit. */
export const CONNECTOR_ACCESS_MAX = 500;

/**
 * Where one connector stands for a new connection in a tenant (round 10, C1: three states):
 * `offered` (Enabled), `not_selected` or `not_available` (Disabled: left out by the tenant's
 * choice, or not available on the platform: switched off, an inactive definition, or a catalog-only
 * seed with no image), or `hidden` (named in the tenant's `hidden` list; wins over the rest and is
 * never listed to the tenant).
 */
export const CONNECTOR_OFFERS = ["offered", "not_selected", "not_available", "hidden"] as const;
export type ConnectorOffer = (typeof CONNECTOR_OFFERS)[number];

/** The 422 `CONNECTOR_NOT_ALLOWED` message for a connector the tenant left out (owner wording). */
export const CONNECTOR_NOT_ALLOWED_MESSAGE =
  "This connector is disabled for your organisation. An admin can enable it in Settings.";
/** The same code's message for a connector the platform does not make available. */
export const CONNECTOR_NOT_AVAILABLE_MESSAGE = "This connector is not available.";
/** A TenantAdmin's write while Skypoint manages the choice (409 `CONFLICT`). */
export const CONNECTOR_ACCESS_LOCKED_MESSAGE =
  "Skypoint manages the connectors offered to your organisation. Contact Skypoint to change them.";
/** The picker's line when the tenant's choice leaves out an available connector. */
export const CONNECTORS_LIMITED_NOTE = "Your admin has limited the connectors available here.";

/** Why a Disabled connector is disabled, in words the server composes (round 10, C1). */
export const CONNECTOR_REASON_SUPPORT = "Contact Skypoint support to enable it.";
export const CONNECTOR_REASON_ADMIN = "Ask your administrator to enable it.";
export const CONNECTOR_REASON_SETTINGS = "Enable it in Settings, Connectors.";
/**
 * A connector whose availability row still says catalog-only (wave 1) cannot be made available
 * (409 `CONFLICT`). Wave 2 (V6) seeds only the release manifest's tested connectors, so such a
 * row is one the runner's one-off prune kept because a connection uses it.
 */
export const CONNECTOR_CATALOG_ONLY_MESSAGE = "That connector's image is not in this deployment.";

/** `GET` and `PUT /api/v2/tenants/:id/connector-access` and `/platform/tenants/:id/connector-access`. */
export interface ConnectorAccessView {
  tenantId: string;
  mode: ConnectorAccessMode;
  /**
   * The stored list in `selected` mode, sorted, including a listed connector the platform has
   * switched off (it comes back when switched on). Kept, and ignored, in `all` mode, so switching
   * back to `selected` restores the last ticks.
   */
  connectors: string[];
  /** Connectors never listed to the tenant (round 10, C1), sorted. Wins over `connectors`. */
  hidden: string[];
  /**
   * The hidden connectors with their display names, so a settings screen can list them to be shown
   * again (`GET /connectors` never returns a hidden one).
   */
  hiddenConnectors: Array<{ name: string; displayName: string }>;
  managedBy: ConnectorAccessManager;
  /** What a new connection in this tenant may use, sorted: exactly what its picker shows. */
  effective: string[];
  /** Null while the tenant has never chosen (the defaults apply). */
  updatedAt: string | null;
}

/** `effectiveConnectorsFor`: the offer for new connections in one tenant. */
export interface EffectiveConnectors {
  /** Sorted connector names a new connection may use. */
  connectors: string[];
  /** True when the tenant's choice leaves out at least one available connector. */
  narrowed: boolean;
  managedBy: ConnectorAccessManager;
}

export const connectorAccessParamsSchema = z.object({ id: z.uuid() });

const connectorNames = z.array(connectorNameParamsSchema.shape.name).max(CONNECTOR_ACCESS_MAX);

/** A name may not be both chosen and hidden. */
const notBothListed = (value: { connectors: string[]; hidden?: string[] | undefined }): boolean => {
  const hidden = new Set(value.hidden ?? []);
  return !value.connectors.some((name) => hidden.has(name));
};
const notBothListedMessage = {
  message: "A connector cannot be both enabled and hidden.",
  path: ["hidden"],
};

/**
 * The TenantAdmin's whole choice, both keys required (a PUT replaces it; no default on an update
 * key). An empty `selected` list is allowed: it offers no connector, and the screen warns first.
 */
export const putConnectorAccessBodySchema = z
  .object({
    mode: z.enum(CONNECTOR_ACCESS_MODES),
    connectors: connectorNames,
    // Round 10 (C1): optional so an older client's body still parses; absent means none hidden.
    hidden: connectorNames.optional(),
  })
  .strict()
  .refine(notBothListed, notBothListedMessage);
export type PutConnectorAccessBody = z.infer<typeof putConnectorAccessBodySchema>;

/** The PlatformAdmin's write from Tenants: the choice and who manages it from now on. */
export const putPlatformConnectorAccessBodySchema = z
  .object({
    mode: z.enum(CONNECTOR_ACCESS_MODES),
    connectors: connectorNames,
    hidden: connectorNames.optional(),
    managedBy: z.enum(CONNECTOR_ACCESS_MANAGERS),
  })
  .strict()
  .refine(notBothListed, notBothListedMessage);
export type PutPlatformConnectorAccessBody = z.infer<typeof putPlatformConnectorAccessBodySchema>;

/** The stored document; anything else in the row reads as the default. */
export const storedConnectorAccessSchema = z.object({
  mode: z.enum(CONNECTOR_ACCESS_MODES),
  connectors: z.array(z.string()),
  /** Round 10 (C1): absent in a row written before, read as none hidden. */
  hidden: z.array(z.string()).optional(),
  managedBy: z.enum(CONNECTOR_ACCESS_MANAGERS).optional(),
});

/** `GET /api/v2/platform/connector-availability` (`platform.connectors.manage`). */
export interface ConnectorAvailabilityView {
  /** Connectors switched off for new connections in every tenant, sorted. */
  notAvailable: string[];
}

export const connectorAvailabilityParamsSchema = connectorNameParamsSchema;

/** `PUT /api/v2/platform/connector-availability/:name`. */
export const putConnectorAvailabilityBodySchema = z.object({ available: z.boolean() }).strict();
export type PutConnectorAvailabilityBody = z.infer<typeof putConnectorAvailabilityBodySchema>;

/**
 * `catalogOnly` (round 10): the definition is seeded for the catalog but no image is imported in
 * this deployment, so it cannot be made available until a release adds it to the manifest.
 */
export const storedConnectorAvailabilitySchema = z.object({
  available: z.boolean(),
  catalogOnly: z.boolean().optional(),
});
